import {useEffect, useState} from "react";
import baseUrl from "../../../baseUrl";
import {BsFillInfoSquareFill, BsExclamationOctagonFill} from "react-icons/bs";
import {Link} from "react-router-dom";
import {requester} from "../../../services/requester";
import {Status} from "../../Status/Status";
import {pravno, adjustedUnsuitable, adjustedSuitable} from "../../../utils/pravno";

export const SettlementCompany = ({
                                      company,
                                      bullstat,
                                      declared_land,
                                      unsuitable_land,
                                      suitable_land,
                                      correction: initialCorrection,
                                      year,
                                      settlement_ekatte,
                                      pendingFlags,
                                      verifiedInfo,
                                      modalInfoHandler
                                  }) => {
    // ЧАСТ К: червената икона ПРЕД иконата за информация — светва, когато този
    // ползвател има замяна, която чака произнасяне тук, или моя замяна, отказана
    // отсреща и още неуредена.
    const needsApproval = pendingFlags?.to_approve?.includes(bullstat);
    const needsAttention = pendingFlags?.my_attention?.includes(bullstat);
    const alertTitle = needsApproval
        ? 'Има замяна, която чака вашето одобрение'
        : 'Ваша замяна е отказана или изтрита в насрещното землище';
    // У1 (23.08.2026): необработваема и годна се показват С отчетена корекция —
    // както на страницата на ползвателя и в справките. Досега тук стояха
    // суровите стойности от базата и същият ред показваше коригирано „Правно",
    // тоест числата на един ред не се връзваха помежду си.
    //
    // Начална стойност идва СЪС СПИСЪКА (сървърът я слага в реда), за да е
    // вярно още при първото изчертаване; след това се презаписва от прясно
    // изтегления ред „други площи".
    const [correction, setCorrection] = useState(initialCorrection || 0);
    const [sumDeals, setSumDeals] = useState(0);
    const [sumOthers, setSumOthers] = useState(0);
    const [sumOthersAlternative, setSumOthersAlternative] = useState(0);
    const [sumDrawing, setSumDrawing] = useState(undefined);
    const [sumForDrawing, setSumForDrawing] = useState(undefined);
    const request = requester();

    useEffect(() => {
            Promise.all([
                request.get(`${baseUrl()}/deals/${year}/${settlement_ekatte}/${bullstat}/sum`),
                request.get(`${baseUrl()}/others/${year}/${settlement_ekatte}/${bullstat}`),
                request.get(`${baseUrl()}/drawings/${year}/${settlement_ekatte}/${bullstat}/sum`)
            ])
                .then(([dataSumDeals, dataSumOthers, dataSumDrawing]) => {
                    const others = dataSumOthers || {};

                    setCorrection(others.correction || 0);
                    setSumDeals(dataSumDeals.sum_deals);
                    setSumOthers(others.total ?? null);
                    setSumOthersAlternative((others.white_spots || 0) + (others.roads || 0));
                    setSumDrawing(dataSumDrawing.sum_drawings);

                    // Н0 (допълнение): и тук сметката минава през общата функция.
                    // Досега беше трупана на ръка чрез `others.total` — вярно, но
                    // седмо копие на същото нещо и извън обхвата, който проверих.
                    setSumForDrawing(pravno({
                        suitableLand: suitable_land,
                        correction: others.correction,
                        sumDeals: dataSumDeals.sum_deals,
                        whiteSpots: others.white_spots,
                        roads: others.roads,
                        othersVal: others.others,
                        othersPlus: others.others_plus,
                    }));
                })
        }, [year, settlement_ekatte, sumDeals, sumOthers]
    );

    const showInfo = () => {
        modalInfoHandler(company);
    };

    let isStartedDrawing = false;
    isStartedDrawing = sumDrawing > 0.01;

    let drawingDiff = sumForDrawing;
    if (isStartedDrawing) {
        drawingDiff = sumForDrawing - sumDrawing;
    }

    // П1: три състояния на реда, в реда на „докъде е стигнала работата":
    //   нищо изчертано        → без фон
    //   изчертано, но не сверено → топлият фон (както досега)
    //   изчертано И ВСИЧКИ редове отметнати „Проверено" → зелено
    // Зеленото е СЪЩИЯТ токен като на отметнатия ред в страницата на ползвателя
    // (--app-verified-bg) — един цвят, едно значение, на двата екрана.
    const allVerified = !!verifiedInfo && verifiedInfo.total > 0
        && verifiedInfo.verified === verifiedInfo.total;

    const rowStyle = allVerified
        ? {background: "var(--app-verified-bg)"}
        : (isStartedDrawing ? {background: "var(--app-surface-2)"} : undefined);

    const rowTitle = verifiedInfo && verifiedInfo.total > 0
        ? (allVerified
            ? `Проверено изцяло — всичките ${verifiedInfo.total} изчертани реда`
            : `Проверени ${verifiedInfo.verified} от ${verifiedInfo.total} изчертани реда`)
        : undefined;

    return (
        <tr style={rowStyle} title={rowTitle}>
            <td style={{color: "var(--app-danger-fg)"}}>
                {(needsApproval || needsAttention) &&
                    <BsExclamationOctagonFill title={alertTitle}/>}
            </td>
            <td style={{color: "var(--app-gold)"}}><BsFillInfoSquareFill onClick={showInfo} data-bs-toggle="modal"
                                                 data-bs-target="#companyInfoModal"/></td>
            <td><Link style={{color: "var(--app-gold)", textDecoration: "none"}}
                      to={`/info/${year}/${settlement_ekatte}/${bullstat}`}>{company.company_name}</Link></td>
            <td>{bullstat}</td>
            <td>{declared_land?.toFixed(2)}</td>
            <td>{unsuitable_land === undefined || unsuitable_land === null
                ? null : adjustedUnsuitable(unsuitable_land, correction).toFixed(2)}</td>
            <td>{suitable_land === undefined || suitable_land === null
                ? null : adjustedSuitable(suitable_land, correction).toFixed(2)}</td>
            <td>{sumDeals ? sumDeals.toFixed(2) : null}</td>
            <td>{sumOthersAlternative ? sumOthersAlternative.toFixed(2) : null}</td>
            <td>{sumForDrawing ? sumForDrawing.toFixed(2) : null}</td>
            <td>{sumDrawing ? sumDrawing.toFixed(2) : null}</td>
            <Status drawingDiff={drawingDiff} />
        </tr>
    )
}